import { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, X, Wallet, Store } from 'lucide-react'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { isWeb3AuthEnabled, loginWithWeb3Auth } from '../lib/web3auth'
import { isWeb3Mode } from '../lib/authMode'
import {
  isGisEnabled,
  renderGoogleButton,
  signInWithGoogleCredential,
  signInWithGooglePopup,
} from '../lib/googleAuth'
import type { MultiFactorResolver, UserCredential } from 'firebase/auth'
import { showToast } from '../components/Toast'
import { ApiError } from '../services/api/_client'
import { useLegalContent } from '../hooks/useLegalContent'
import { api } from '../services/api'
import PasskeyEnrollmentModal from '../components/PasskeyEnrollmentModal'
import TotpEnrollmentModal from '../components/TotpEnrollmentModal'
import SigningInOverlay from '../components/SigningInOverlay'
import { markTotpEnrolled } from '../hooks/useEnrollmentGate'
import TotpSignInModal from '../components/TotpSignInModal'
import { isMfaRequiredError, getTotpResolver, hasTotpFactor } from '../lib/totpMfa'
import { auth } from '../lib/firebase'

// #2655 プレビューのビルドだけに出す、テスト用アカウントのメールとパスワードのログイン。
// `import.meta.env.VITE_APP_ENV` はビルド時に文字列に置き換わるので、プレビュー以外のビルドでは
// この式が null に畳まれ、部品の chunk ごと成果物に入らない (デプロイの前に
// scripts/check-no-preview-only.cjs が確かめる)。**判定を実行時の関数に書き換えないこと** —
// 畳まれなくなり、本番の成果物に入る。
const PreviewPasswordLogin = import.meta.env.VITE_APP_ENV === 'preview'
  ? lazy(() => import('../components/PreviewPasswordLogin'))
  : null

// メールかパスワードが違うときの Firebase のエラー (ほかの失敗をこれと同じ文言にしない。通信の失敗や
// 試しすぎを「パスワードが違う」と言うと、貼り直して余計に止められる)
const PREVIEW_BAD_CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-email',
])

export default function Login() {
  const navigate = useNavigate()
  const { login } = useStore()
  const { t, locale } = useI18n()
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  // GIS コールバック用の isLoggingIn の写し。false に戻すのは下の effect だけ
  // (setIsLoggingIn(false) の箇所が多いので、各所で ref を戻す書き忘れを防ぐ)
  const loggingInRef = useRef(false)
  useEffect(() => {
    if (!isLoggingIn) loggingInRef.current = false
  }, [isLoggingIn])
  // #815: Firebase が `auth/multi-factor-auth-required` を投げた時の TOTP
  // resolver。non-null の間 TotpSignInModal を表示し、解決で通常 flow に合流。
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null)
  const [modalType, setModalType] = useState<'terms' | 'privacy' | 'tokushoho' | 'refundPolicy' | null>(null)
  const web3Mode = isWeb3Mode()

  // Pre-initialize Web3Auth on page load (Web3 mode only)
  useEffect(() => {
    if (web3Mode && isWeb3AuthEnabled()) {
      import('../lib/web3auth').then(({ initWeb3Auth }) => {
        initWeb3Auth().catch(() => {})
      })
    }
  }, [web3Mode])

  // #734: enrollment_mode に応じて signup / login 後に Passkey 登録を促す.
  // 必須 mode は dismiss 不可、optional は dismiss 可能。
  const [passkeyModalOpen, setPasskeyModalOpen] = useState(false)
  const [passkeyEnforced, setPasskeyEnforced] = useState(false)
  const [pendingIsNewUser, setPendingIsNewUser] = useState<boolean | null>(null)
  // #838: Login フローの passkey ゲートでも、ceremony 失敗/freeze 時に
  // TOTP へ退避(RequireAuth 側と同方針)。totpFallbackEnabled OFF では
  // escape を出さない = byte-for-byte 不変。
  const [totpFallbackOn, setTotpFallbackOn] = useState(false)
  const [escapedToTotp, setEscapedToTotp] = useState(false)

  const navigateAfterLogin = useCallback((isNewUser: boolean) => {
    if (isNewUser) {
      navigate('/setup/profile', { replace: true })
      return
    }
    localStorage.setItem('hasCompletedSetup', 'true')
    // Return to an intended destination (e.g. /pay/:storeId) if one was stashed
    // before this login flow started.
    const redirect = sessionStorage.getItem('postLoginRedirect')
    if (redirect) {
      sessionStorage.removeItem('postLoginRedirect')
      navigate(redirect, { replace: true })
      return
    }
    navigate('/home', { replace: true })
  }, [navigate])

  // #734: Login 後の Passkey enrollment 判定を navigate の前段に挟む.
  //   - required_all + credentials 0 → 必須 modal
  //   - required_new_users + isNewUser + credentials 0 → 必須 modal
  //   - optional + credentials 0 → dismissible modal (= signup 流の促進)
  //   - 上記以外 → 通常 navigate
  // API call が失敗したら fail-safe で通常 navigate (= login を詰まらせない).
  const navigateWithPasskeyGate = useCallback(async (isNewUser: boolean) => {
    try {
      const config = await api.getWebauthnConfig()
      setTotpFallbackOn(config.totpFallbackEnabled === true)
      if (config.enrollmentMode === 'disabled') {
        navigateAfterLogin(isNewUser)
        return
      }
      const credsResp = await api.listWebauthnCredentials().catch(() => ({ credentials: [] }))
      const hasCreds = (credsResp.credentials || []).length > 0
      if (hasCreds) {
        navigateAfterLogin(isNewUser)
        return
      }
      // #815/#838/#869c: TOTP fallback flag ON のとき、TOTP 第2要素登録済なら
      // passkey 不要として通過。`useEnrollmentGate.evaluate()` と同じ判定を
      // Login.tsx の独立 passkey gate でも適用しないと、TOTP enrolled user が
      // 「すでに enroll 済なのに modal 強制」+「Firebase が
      // `auth/maximum-second-factor-count-exceeded` で TOTP 再 enroll 拒否」
      // で詰む(#869 ③ の真因)。
      if (config.totpFallbackEnabled) {
        try {
          await auth.authStateReady()
        } catch { /* fall through */ }
        await auth.currentUser?.reload().catch(() => {})
        if (hasTotpFactor(auth.currentUser)) {
          navigateAfterLogin(isNewUser)
          return
        }
      }
      const enforced =
        config.enrollmentMode === 'required_all' ||
        (config.enrollmentMode === 'required_new_users' && isNewUser)
      const shouldPromote = enforced || config.enrollmentMode === 'optional'
      if (!shouldPromote) {
        navigateAfterLogin(isNewUser)
        return
      }
      setPendingIsNewUser(isNewUser)
      setPasskeyEnforced(enforced)
      setPasskeyModalOpen(true)
    } catch (e) {
      console.warn('[Login] passkey gate failed, falling through:', e)
      navigateAfterLogin(isNewUser)
    }
  }, [navigateAfterLogin])

  // Passkey modal 終了 → 控えていた navigation を実行.
  const handlePasskeyModalClose = useCallback(() => {
    setPasskeyModalOpen(false)
    setEscapedToTotp(false)
    const target = pendingIsNewUser
    setPendingIsNewUser(null)
    if (target !== null) navigateAfterLogin(target)
  }, [pendingIsNewUser, navigateAfterLogin])

  /**
   * #1568 退会済みアカウントでのログイン。
   *
   * **「いつから使えるか」を言い切る。** 「失敗しました」だけだと問い合わせに
   * なる。復帰 (= 履歴ごと元に戻す) は用意していない — 退会したのに再開できる
   * 作りは、評価のリセットやブロック逃れ、初回特典の再取得に使われるため。
   * 保存期間が過ぎればサーバ側で記録が消え、同じメールでも新規として登録できる。
   */
  const handleWithdrawnIfNeeded = useCallback((err: unknown): boolean => {
    if (!(err instanceof ApiError) || err.code !== 'account_withdrawn') return false
    const canRegisterAfter = err.detail?.canRegisterAfter as string | undefined
    showToast({
      type: 'error',
      message: canRegisterAfter
        ? t('login.withdrawnUntil', { date: canRegisterAfter })
        : t('login.withdrawn'),
    })
    setIsLoggingIn(false)
    return true
  }, [t])

  const finishFirebaseLogin = useCallback(
    async (user: { displayName: string | null; email: string | null }) => {
      try {
        const loginResult = await login({
          name: user.displayName || 'User',
          email: user.email || '',
        })
        await navigateWithPasskeyGate(loginResult.isNewUser)
      } catch (err) {
        if (handleWithdrawnIfNeeded(err)) return
        console.error('[Login] Firebase login failed:', err)
        showToast({ type: 'error', message: 'Login cancelled or failed' })
        setIsLoggingIn(false)
      }
    },
    [login, navigateWithPasskeyGate, handleWithdrawnIfNeeded],
  )

  // #815: sign-in が `auth/multi-factor-auth-required` の時だけ true を返し
  // TotpSignInModal を立ち上げる。TOTP hint が無い MFA (= 想定外。SMS 等は
  // 未対応) は false を返し、呼び元の通常エラー処理に委ねる (fail-safe)。
  const handleMfaIfRequired = useCallback((err: unknown): boolean => {
    if (!isMfaRequiredError(err)) return false
    const resolver = getTotpResolver(err)
    if (!resolver) return false
    setMfaResolver(resolver)
    return true
  }, [])

  // TOTP 解決成功 → resolver の UserCredential で通常 Firebase login に合流。
  const handleMfaResolved = useCallback(
    (cred: UserCredential) => {
      setMfaResolver(null)
      void finishFirebaseLogin(cred.user)
    },
    [finishFirebaseLogin],
  )

  // ユーザーがログイン中断 → modal を閉じ初期状態へ戻す。
  const handleMfaCancel = useCallback(() => {
    setMfaResolver(null)
    setIsLoggingIn(false)
  }, [])

  // Popup fallback used when GIS is unavailable (emulator / missing client id).
  const handleFirebasePopupLogin = useCallback(async () => {
    if (isLoggingIn) return
    setIsLoggingIn(true)
    try {
      const credential = await signInWithGooglePopup()
      await finishFirebaseLogin(credential.user)
    } catch (err) {
      if (handleMfaIfRequired(err)) return
      if (handleWithdrawnIfNeeded(err)) return
      console.error('[Login] Firebase popup login failed:', err)
      showToast({ type: 'error', message: 'Login cancelled or failed' })
      setIsLoggingIn(false)
    }
  }, [finishFirebaseLogin, handleMfaIfRequired, handleWithdrawnIfNeeded, isLoggingIn])

  // GIS button mount target — only used in default (non-Web3) mode when GIS
  // is enabled. The button is rendered by Google's SDK into this div.
  const gisContainerRef = useRef<HTMLDivElement | null>(null)
  const gisEnabled = !web3Mode && isGisEnabled()

  useEffect(() => {
    if (!gisEnabled) return
    const el = gisContainerRef.current
    if (!el) return
    const teardown = renderGoogleButton(
      el,
      { theme: 'outline', size: 'large', shape: 'rectangular', width: 320, locale },
      async (gisIdToken) => {
        // GIS のコールバックは effect 実行時に一度だけ捕まるので state が見えない。
        // ref で二重実行を防ぐ (処理中に Google のボタンをもう一度押された場合)
        if (loggingInRef.current) return
        loggingInRef.current = true
        setIsLoggingIn(true)
        try {
          const cred = await signInWithGoogleCredential(gisIdToken)
          await finishFirebaseLogin(cred.user)
        } catch (err) {
          if (handleMfaIfRequired(err)) return
          console.error('[Login] GIS credential exchange failed:', err)
          showToast({ type: 'error', message: 'Login cancelled or failed' })
          setIsLoggingIn(false)
        }
      },
      (err) => {
        console.error('[Login] GIS button error:', err)
        showToast({ type: 'error', message: 'Login cancelled or failed' })
        setIsLoggingIn(false)
      },
    )
    return teardown
  }, [gisEnabled, locale, finishFirebaseLogin, handleMfaIfRequired])

  const handleWeb3Login = useCallback(async () => {
    if (isLoggingIn) return
    setIsLoggingIn(true)
    try {
      if (isWeb3AuthEnabled()) {
        const result = await loginWithWeb3Auth()
        const loginResult = await login({
          name: result.user.name,
          email: result.user.email,
          walletAddress: result.walletAddress,
        })
        await navigateWithPasskeyGate(loginResult.isNewUser)
      } else {
        // Demo mode fallback
        const loginResult = await login()
        await navigateWithPasskeyGate(loginResult.isNewUser)
      }
    } catch (err) {
      if (handleWithdrawnIfNeeded(err)) return
      console.error('[Login] Web3 login failed:', err)
      showToast({ type: 'error', message: 'Login cancelled or failed' })
      setIsLoggingIn(false)
    }
  }, [login, navigateWithPasskeyGate, handleWithdrawnIfNeeded, isLoggingIn])

  const handleLogin = web3Mode ? handleWeb3Login : handleFirebasePopupLogin

  // #2655 プレビューのテスト用アカウント。サインインの後は Google と同じ道 (finishFirebaseLogin) に合流する。
  // 押した時点で isLoggingIn を立てる (サインインの最中・TOTP の画面の間に Google のボタンで 2 本目を始めさせない)
  const handlePreviewStart = useCallback(() => setIsLoggingIn(true), [])

  const handlePreviewSignedIn = useCallback(async (credential: UserCredential) => {
    await finishFirebaseLogin(credential.user)
  }, [finishFirebaseLogin])

  const handlePreviewFailed = useCallback((err: unknown) => {
    // TOTP の画面に進むときは立てたまま (Google の道と同じ。閉じれば handleMfaCancel が戻す)
    if (handleMfaIfRequired(err)) return
    setIsLoggingIn(false)
    console.error('[Login] preview password login failed:', err)
    const code = (err as { code?: string } | null)?.code || ''
    const message = PREVIEW_BAD_CREDENTIAL_CODES.has(code)
      ? t('previewLogin.failed')
      : code === 'auth/too-many-requests'
        ? t('previewLogin.tooManyRequests')
        : t('previewLogin.otherError', { code: code || 'unknown' })
    showToast({ type: 'error', message })
  }, [handleMfaIfRequired, t])

  // 本文は開く時にだけ読み込む (メインの JS に入れない — hooks/useLegalContent.ts)
  const legalContent = useLegalContent(locale, !!modalType)

  const modalTitle = modalType ? t(`login.${modalType}`) : ''
  const modalBody = modalType ? (legalContent?.[modalType] ?? t('common.loading')) : ''

  return (
    <div className="relative flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      {/* Back Button */}
      <div className="px-4 pt-4">
        <button
          onClick={() => navigate(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full tap-effect hover:bg-gray-100 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft size={22} className="text-text-primary" />
        </button>
      </div>

      {/* Logo & Heading */}
      <div className="flex flex-col items-center px-6 pt-8 pb-6">
        <h1 className="animate-scale-in font-display text-3xl font-bold tracking-tight">
          <span className="text-navy">YOKOSO</span>{' '}
          <span className="text-yokoso-red">PAY</span>
        </h1>

        <h2
          className="animate-fade-in mt-8 font-display text-[24px] font-bold text-text-primary text-center"
          style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
        >
          {t('login.title')}
        </h2>

        <p
          className="animate-fade-in mt-2 font-body text-[15px] text-text-secondary text-center max-w-[300px] leading-relaxed"
          style={{ animationDelay: '0.25s', animationFillMode: 'both' }}
        >
          {t('login.subtitle')}
        </p>
      </div>

      {/* Login Buttons */}
      <div
        className="animate-fade-in flex flex-col gap-3 px-6"
        style={{ animationDelay: '0.35s', animationFillMode: 'both' }}
      >
        {web3Mode ? (
          <>
            {/* Web3 mode: all social login buttons */}
            {/* Google */}
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="tap-effect flex h-[52px] w-full items-center rounded-xl border border-[#E5E5E5] bg-white px-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <span
                className="flex h-6 w-6 items-center justify-center font-display text-[18px] font-bold"
                style={{
                  background: 'linear-gradient(135deg, #4285F4 25%, #EA4335 25%, #EA4335 50%, #FBBC05 50%, #FBBC05 75%, #34A853 75%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                G
              </span>
              <span className="ml-3 font-body text-[15px] font-medium text-text-primary">
                {t('login.google')}
              </span>
            </button>

            {/* Apple */}
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="tap-effect flex h-[52px] w-full items-center rounded-xl border border-[#E5E5E5] bg-white px-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="ml-3 font-body text-[15px] font-medium text-text-primary">
                {t('login.apple')}
              </span>
            </button>

            {/* Facebook */}
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="tap-effect flex h-[52px] w-full items-center rounded-xl border border-[#E5E5E5] bg-white px-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="ml-3 font-body text-[15px] font-medium text-text-primary">
                {t('login.facebook')}
              </span>
            </button>

            {/* X (Twitter) */}
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="tap-effect flex h-[52px] w-full items-center rounded-xl border border-[#E5E5E5] bg-white px-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="ml-3 font-body text-[15px] font-medium text-text-primary">
                {t('login.x')}
              </span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 py-1">
              <div className="h-px flex-1 bg-[#E5E5E5]" />
              <span className="font-body text-[13px] text-text-tertiary">{t('login.or')}</span>
              <div className="h-px flex-1 bg-[#E5E5E5]" />
            </div>

            {/* Email */}
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="tap-effect flex h-[52px] w-full items-center justify-center rounded-xl border border-[#E5E5E5] bg-white px-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="font-body text-[15px] font-medium text-text-primary">
                {isLoggingIn ? t('login.signingIn') : t('login.email')}
              </span>
            </button>
          </>
        ) : gisEnabled ? (
          <>
            {/* Default mode + GIS: Google Identity Services renders the
                button itself (FedCM, no popup → no COOP warning). */}
            <div className="flex justify-center">
              <div ref={gisContainerRef} aria-busy={isLoggingIn} />
            </div>
          </>
        ) : (
          <>
            {/* Default mode + emulator/missing client id: keep the popup
                fallback so local development with the Auth Emulator works. */}
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="tap-effect flex h-[52px] w-full items-center justify-center gap-3 rounded-xl border border-[#E5E5E5] bg-white px-4 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="font-body text-[15px] font-medium text-text-primary">
                {isLoggingIn ? t('login.signingIn') : t('login.google')}
              </span>
            </button>
          </>
        )}
        {PreviewPasswordLogin && !web3Mode && (
          <Suspense fallback={null}>
            <PreviewPasswordLogin
              disabled={isLoggingIn}
              onStart={handlePreviewStart}
              onSignedIn={handlePreviewSignedIn}
              onFailed={handlePreviewFailed}
            />
          </Suspense>
        )}
      </div>

      {/* Wallet auto-creation note (Web3 mode only) */}
      {web3Mode && (
        <div
          className="animate-fade-in mx-6 mt-4 flex items-start gap-2.5 rounded-xl bg-navy/5 px-4 py-3"
          style={{ animationDelay: '0.45s', animationFillMode: 'both' }}
        >
          <Wallet size={16} className="mt-0.5 shrink-0 text-navy/60" />
          <p className="font-body text-[12px] leading-relaxed text-text-secondary">
            {t('login.walletNote')}
          </p>
        </div>
      )}

      {/* #1246: ゲスト入口 — 登録前にアプリを見て回れる (ホームへ)。
          「使いたくなった行動の瞬間 (決済/予約確定/注文送信/保存)」に
          各画面のゲートが postLoginRedirect 付きでここへ連れ戻す設計。 */}
      <div className="mt-5 px-6 text-center">
        <button
          onClick={() => navigate('/home')}
          className="tap-effect font-body text-[14px] font-medium text-text-secondary underline underline-offset-4"
        >
          {t('login.continueAsGuest')}
        </button>
      </div>

      {/* Store Owner Entry (#189) — pill button so merchants don't miss the
          entry point. Distinct from the primary Google button via muted
          navy border + smaller footprint, so customer users are unlikely
          to mistap it. */}
      <div className="mt-auto px-6 pb-2 pt-6 text-center">
        <button
          onClick={() => navigate('/store/login')}
          className="tap-effect inline-flex items-center gap-2 rounded-full border border-navy/30 bg-white px-5 py-2 font-body text-[13px] font-medium text-navy transition-colors hover:bg-navy/5"
        >
          <Store size={14} />
          {t('login.storeOwnerEntry')}
        </button>
      </div>

      {/* Bottom Terms */}
      <div className="px-6 pb-8 pt-2">
        <p className="text-center font-body text-[12px] leading-relaxed text-text-tertiary">
          {t('login.termsText', {
            terms: `<terms>${t('login.terms')}</terms>`,
            privacy: `<privacy>${t('login.privacy')}</privacy>`,
          }).split(/(<terms>.*?<\/terms>|<privacy>.*?<\/privacy>)/).map((part, i) => {
            const termsMatch = part.match(/^<terms>(.*)<\/terms>$/)
            const privacyMatch = part.match(/^<privacy>(.*)<\/privacy>$/)
            if (termsMatch) return (
              <span
                key={i}
                className="text-navy-light underline underline-offset-2 cursor-pointer"
                onClick={() => setModalType('terms')}
              >
                {termsMatch[1]}
              </span>
            )
            if (privacyMatch) return (
              <span
                key={i}
                className="text-navy-light underline underline-offset-2 cursor-pointer"
                onClick={() => setModalType('privacy')}
              >
                {privacyMatch[1]}
              </span>
            )
            return part
          })}
        </p>
        <p className="mt-2 text-center font-body text-[11px] leading-relaxed text-text-tertiary">
          <span
            className="text-navy-light underline underline-offset-2 cursor-pointer"
            onClick={() => setModalType('tokushoho')}
          >
            {t('login.tokushoho')}
          </span>
          <span className="mx-2 text-text-disabled">·</span>
          <span
            className="text-navy-light underline underline-offset-2 cursor-pointer"
            onClick={() => setModalType('refundPolicy')}
          >
            {t('login.refundPolicy')}
          </span>
        </p>
      </div>

      {/* Legal Document Modal */}
      {modalType && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          onClick={() => setModalType(null)}
        >
          <div
            className="animate-slide-up w-full max-w-[430px] rounded-t-2xl bg-white shadow-xl"
            style={{ maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="font-display text-[16px] font-bold text-text-primary">
                {modalTitle}
              </h3>
              <button
                onClick={() => setModalType(null)}
                className="tap-effect flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <X size={18} className="text-text-secondary" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto px-5 py-4" style={{ maxHeight: 'calc(85vh - 64px)' }}>
              <pre className="whitespace-pre-wrap font-body text-[13px] leading-relaxed text-text-secondary">
                {modalBody}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* #734: Login 直後の Passkey enrollment 促進 (= signup flow 統合) */}
      {passkeyModalOpen && escapedToTotp && totpFallbackOn ? (
        /* #838: passkey 失敗/freeze → TOTP 退避(Login ゲート版)。
           enroll 完了で markTotpEnrolled() し、passkey 成功と同じ
           navigation 確定経路へ。 */
        <TotpEnrollmentModal
          open
          onEnrolled={() => {
            markTotpEnrolled()
            handlePasskeyModalClose()
          }}
        />
      ) : (
        <PasskeyEnrollmentModal
          open={passkeyModalOpen}
          enforced={passkeyEnforced}
          onClose={handlePasskeyModalClose}
          onEnrolled={() => {
            // 登録成功 → modal は自動 close (= EnrollmentModal 側で onClose 呼ぶ)
            // navigate は handlePasskeyModalClose で確定する
          }}
          // #838: flag ON のときだけ escape を渡す。PasskeyModal は ceremony
          // 失敗/freeze 後にのみ TOTP 退避を表示(#789: 事前対等選択にしない)。
          onFallbackToTotp={
            totpFallbackOn ? () => setEscapedToTotp(true) : undefined
          }
        />
      )}

      {/* #815: TOTP enroll 済ユーザーの sign-in 第2要素 (Firebase が
          auth/multi-factor-auth-required を投げた時のみ resolver が入る) */}
      <TotpSignInModal
        resolver={mfaResolver}
        onResolved={handleMfaResolved}
        onCancel={handleMfaCancel}
      />

      {/* Google から戻った後の待ち時間に、押せるログイン画面を見せない。
          Passkey / TOTP のモーダルはログイン処理中のまま出るので、その間は消す */}
      <SigningInOverlay
        show={isLoggingIn && !passkeyModalOpen && !mfaResolver}
        message={t('login.signingIn')}
      />
    </div>
  )
}
